/* eslint-disable @next/next/no-img-element */
import classNames from "classnames/bind";
import DynamicPhoneNumber from "common/blocks/DynamicPhoneNumber";
import UIIcon from "common/blocks/UIIcon";
import SmartLink from "common/wrapper/SmartLink";
import { motion } from "framer-motion";
import useHasMounted from "hooks/useHasMounted/useHasMounted";
import { useEffect, useState, MouseEvent, FC } from "react";
import type { ResolvedBaseData } from "resolvers/base/baseResolver";
import { ContentTypeCodenames } from "services/cms/models/contentTypeCodenames";
import getOnlinePhoneLinesText, { PhoneLinesInfo } from "utils/getOnlineOpeningTimes";
import getPathByType, { UrlPathTypes } from "utils/getPathByType";
import HeaderBurgerMenuButton from "./components/HeaderBurgerMenuButton";
import HeaderMegaMenu from "./components/HeaderMegaMenu";
import HeaderMobileMenu from "./components/HeaderMobileMenu";
import HeaderNavItems from "./components/HeaderNavItems";
import HeaderPromoStrip from "./components/HeaderPromoStrip";
import HeaderShortListButton from "./components/HeaderShortListButton";
import HeaderSubmenu from "./components/HeaderSubmenu";
import styles from "./Header.module.scss";

const cx = classNames.bind(styles);

const variants = {
    open: { opacity: 1, x: 0, z: 0, y: 0 },
    closed: { opacity: 0, x: 0, z: 0, y: 0 },
};

export type HeaderProps = ResolvedBaseData & {
    loggedIn: boolean;
    prefetch?: boolean;
    disableShortlist?: boolean;
    overideShowRotatingBanners?: boolean;
};

let mainNavTimer: NodeJS.Timeout;
let megaMenuTimer: NodeJS.Timeout;

const Header: FC<HeaderProps> = ({
    header,
    loggedIn,
    prefetch = false,
    disableShortlist = false,
}) => {
    const {
        mobilePromos,
        contactInfo,
        promo,
        menuHeadings,
        mainMenu,
        subMenu,
        megaMenu,
        stockNumberSearch,
        onlineOpeningTimes,
        phoneLinesClosedText,
        phoneLinesOpenText,
    } = header ?? {};

    const [phoneLinesInfo, setPhoneLinesInfo] = useState<PhoneLinesInfo | null>(null);
    const onlinePhoneLinesText = phoneLinesInfo?.onlinePhoneLinesText;
    const isClosed = phoneLinesInfo?.isClosed;

    useEffect(() => {
        const getPhoneLinesInfo = () => {
            const phoneLinesInfo = getOnlinePhoneLinesText(
                onlineOpeningTimes,
                phoneLinesOpenText,
                phoneLinesClosedText
            );
            setPhoneLinesInfo(phoneLinesInfo);
        };
        getPhoneLinesInfo();
    }, []);

    const noPromoTop = !promo || (!promo.link && !promo.text);

    const delay = 100;

    const hasMounted = useHasMounted();
    const [activeMegaMenu, setActiveMegaMenu] = useState<string | null>(null);
    const [activeMobileMenu, setActiveMobileMenu] = useState<boolean>(false);
    const [activeDesktopSubMenu, setActiveDesktopSubMenu] = useState<string | null>(null);
    const [isFocusStockInput, setIsFocusStockInput] = useState<boolean>(false);

    const setActiveDesktopSecondarySubMenu = (
        activeDesktopSubMenu: string | null,
        instant?: boolean
    ) => {
        clearTimeout(mainNavTimer);
        mainNavTimer = setTimeout(
            () => {
                setActiveDesktopSubMenu(activeDesktopSubMenu);
            },
            instant ? 0 : delay
        );
    };

    const handleToggleMobileMenu = (event: MouseEvent<HTMLButtonElement> | Event) => {
        setActiveMobileMenu(!activeMobileMenu);
        event.stopPropagation();
    };

    const handleToggleMegaMenu = (key: string | null, instant?: boolean) => {
        clearTimeout(megaMenuTimer);
        if (activeMegaMenu !== key) {
            megaMenuTimer = setTimeout(
                () => {
                    setActiveMegaMenu(key === activeMegaMenu ? null : key);
                },
                instant ? 0 : delay
            );
        }
    };

    const handleCancelToggleSubMenu = () => {
        clearTimeout(mainNavTimer);
    };

    const handleCancelToggleMegaMenu = () => {
        clearTimeout(megaMenuTimer);
    };

    useEffect(() => {
        const closeMegaMenu = (event: KeyboardEvent) =>
            event.key === "Escape" && setActiveMegaMenu(null);
        window.addEventListener("keydown", closeMegaMenu);
        return () => {
            window.removeEventListener("keydown", closeMegaMenu);
        };
    }, []);

    useEffect(() => {
        const closeMenus = () => {
            if (
                !isFocusStockInput &&
                activeMegaMenu !== ContentTypeCodenames.StockNumberSearch &&
                !activeMobileMenu
            ) {
                setActiveMegaMenu(null);
                setActiveMobileMenu(false);
            }
        };

        window.addEventListener("resize", closeMenus);

        return () => {
            window.removeEventListener("resize", closeMenus);
        };
    }, [activeMegaMenu, activeMobileMenu, isFocusStockInput]);

    return (
        <>
            {!!activeMegaMenu && (
                <div
                    className={cx("invisiClose", "invisiCloseTop", { noPromoTop })}
                    onMouseEnter={() => handleToggleMegaMenu(null, true)}
                />
            )}
            <header className={cx("fixed")}>
                {!noPromoTop && !!promo && <HeaderPromoStrip promo={promo} />}
                <div className={cx("wrapper")}>
                    <div className={cx("widthConstrain")}>
                        <div className={cx("header")}>
                            <HeaderBurgerMenuButton onClick={handleToggleMobileMenu} />
                            <div className={cx("logoContainer")}>
                                <SmartLink href="/" prefetch={!!prefetch}>
                                    <a
                                        className={cx("logoLink")}
                                        role="navigation"
                                        aria-label="Main"
                                    >
                                        <img
                                            className={cx("logo")}
                                            alt="Motorpoint logo"
                                            src="https://images.motorpoint.co.uk/logos/motorpoint/motorpoint-logo-svg.svg"
                                            title="Motorpoint - The Car Supermarket"
                                            width="195"
                                            height="53"
                                        />
                                    </a>
                                </SmartLink>
                            </div>
                            <div className={cx("menuContainer")}>
                                <HeaderNavItems
                                    navItems={mainMenu ?? []}
                                    activeSubMenu={activeDesktopSubMenu}
                                    activeMegaMenu={activeMegaMenu}
                                    handleToggleSubMenu={setActiveDesktopSecondarySubMenu}
                                    handleToggleMegaMenu={handleToggleMegaMenu}
                                    handleCancelToggleSubMenu={handleCancelToggleSubMenu}
                                    handleCancelToggleMegaMenu={handleCancelToggleMegaMenu}
                                />
                            </div>
                            <div className={cx("buttonContainer")}>
                                <div className={cx("phoneLinesContainer")}>
                                    <SmartLink
                                        href={`tel:${contactInfo.phoneNumber}`}
                                        prefetch={!!prefetch}
                                    >
                                        <a className={cx("button")} aria-label="Call Motorpoint">
                                            <DynamicPhoneNumber
                                                isLink={false}
                                                className={cx("text")}
                                            />
                                            <div
                                                className={cx(
                                                    "iconButton",
                                                    "iconButtonBackground",
                                                    "phoneIcon"
                                                )}
                                            >
                                                {hasMounted && (
                                                    <UIIcon color="white" icon="Phone" />
                                                )}
                                            </div>
                                        </a>
                                    </SmartLink>
                                    {!!phoneLinesInfo && (
                                        <div className={cx("openingTimesContainer")}>
                                            <span
                                                className={cx("openingTimesIcon", {
                                                    isOpen: !isClosed,
                                                })}
                                            ></span>
                                            <span className={cx("openingTimesText")}>
                                                {onlinePhoneLinesText}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                <SmartLink
                                    href={
                                        !loggedIn
                                            ? getPathByType({
                                                  type: UrlPathTypes.MyMotorpointLoginPage,
                                              })
                                            : getPathByType({
                                                  type: UrlPathTypes.MyMotorpointMyActivityPage,
                                                  page: "my-activity",
                                              })
                                    }
                                    prefetch={!!prefetch}
                                >
                                    <a
                                        className={cx("button", "buttonUser")}
                                        aria-label={
                                            loggedIn ? "MyMotorpoint" : "Sign in / Register"
                                        }
                                    >
                                        {disableShortlist && (
                                            <span className={cx("text", "buttonUserText")}>
                                                {loggedIn ? "MyMotorpoint" : "Sign in / Register"}
                                            </span>
                                        )}
                                        <div className={cx("iconButton", "iconButtonBackground")}>
                                            {hasMounted && <UIIcon color="white" icon="Person" />}
                                        </div>
                                    </a>
                                </SmartLink>
                                {!disableShortlist && <HeaderShortListButton />}
                            </div>
                        </div>
                    </div>
                </div>
                {activeDesktopSubMenu && (
                    <HeaderSubmenu
                        navItems={subMenu}
                        handleToggleMegaMenu={handleToggleMegaMenu}
                        activeSubMenuId={activeDesktopSubMenu}
                        activeMegaMenuId={activeMegaMenu}
                    />
                )}
                <HeaderMegaMenu
                    activeMenuId={activeMegaMenu}
                    megaMenuContent={megaMenu ?? []}
                    stockNumberSearch={stockNumberSearch}
                    changeFocusStockInput={setIsFocusStockInput}
                />
                <motion.div
                    initial="closed"
                    className={styles.headerMobileMenu}
                    animate={activeMobileMenu ? "open" : "closed"}
                    variants={variants}
                    transition={{ duration: 0.2 }}
                >
                    <HeaderMobileMenu
                        handleToggleMobileMenu={handleToggleMobileMenu}
                        stockNumberSearch={stockNumberSearch}
                        megaMenuItems={megaMenu ?? []}
                        mobileMenuOpen={activeMobileMenu}
                        mobilePromos={mobilePromos}
                        navItems={mainMenu ?? []}
                        subMenuItems={subMenu ?? []}
                        menuHeadings={menuHeadings}
                        isPhoneLinesOpen={!isClosed}
                        phoneLinesText={onlinePhoneLinesText}
                    />
                </motion.div>
            </header>
            {(!!activeMegaMenu || !!activeDesktopSubMenu) && (
                <div
                    className={cx("invisiClose", "invisiCloseBottom")}
                    onMouseEnter={() => {
                        setActiveDesktopSecondarySubMenu(null, true);
                        handleToggleMegaMenu(null, true);
                    }}
                />
            )}
        </>
    );
};

export default Header;
